/**
 * usePendingMembers Hook
 * Manage pre-registered family members
 */

import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type {
  PendingMemberWithRelations,
  CreatePendingMemberData,
  UpdatePendingMemberData,
} from "../types";

interface UsePendingMembersReturn {
  pendingMembers: PendingMemberWithRelations[];
  isLoading: boolean;
  error: string | null;
  fetchPendingMembers: () => Promise<void>;
  createPendingMember: (data: CreatePendingMemberData) => Promise<boolean>;
  updatePendingMember: (id: string, data: UpdatePendingMemberData) => Promise<boolean>;
  deletePendingMember: (id: string) => Promise<boolean>;
  linkUserToPendingMember: (userId: string, pendingMemberId: string) => Promise<boolean>;
  stats: {
    total: number;
    claimed: number;
    pending: number;
  };
}

export const usePendingMembers = (weekendId?: string): UsePendingMembersReturn => {
  const { user, activeWeekend } = useAuth();
  const effectiveWeekendId = weekendId || activeWeekend?.id;

  const [pendingMembers, setPendingMembers] = useState<PendingMemberWithRelations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all pending members
   */
  const fetchPendingMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let query = supabase
        .from("pending_members")
        .select(`
          *,
          team:teams(id, name, color),
          weekend:family_weekends(id, name),
          invited_by_profile:profiles!pending_members_invited_by_fkey(id, full_name, nickname),
          claimed_by_profile:profiles!pending_members_claimed_by_fkey(id, full_name, nickname, email)
        `)
        .order("created_at", { ascending: false });

      // Filter by weekend if provided
      if (effectiveWeekendId) {
        query = query.eq("weekend_id", effectiveWeekendId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setPendingMembers(data || []);
    } catch (err) {
      console.error("Error fetching pending members:", err);
      setError("Kon leden niet ophalen");
    } finally {
      setIsLoading(false);
    }
  }, [effectiveWeekendId]);

  /**
   * Create a new pending member
   */
  const createPendingMember = useCallback(
    async (data: CreatePendingMemberData): Promise<boolean> => {
      try {
        const { error: insertError } = await supabase
          .from("pending_members")
          .insert({
            ...data,
            weekend_id: data.weekend_id || effectiveWeekendId,
            invited_by: user?.id,
          });

        if (insertError) {
          if (insertError.code === "23505") {
            throw new Error("Dit e-mailadres is al geregistreerd");
          }
          throw insertError;
        }

        await fetchPendingMembers();
        return true;
      } catch (err: any) {
        console.error("Error creating pending member:", err);
        setError(err.message || "Kon lid niet toevoegen");
        return false;
      }
    },
    [user?.id, effectiveWeekendId, fetchPendingMembers]
  );

  /**
   * Update a pending member
   */
  const updatePendingMember = useCallback(
    async (id: string, data: UpdatePendingMemberData): Promise<boolean> => {
      try {
        const { error: updateError } = await supabase
          .from("pending_members")
          .update(data)
          .eq("id", id);

        if (updateError) throw updateError;

        await fetchPendingMembers();
        return true;
      } catch (err) {
        console.error("Error updating pending member:", err);
        setError("Kon lid niet bijwerken");
        return false;
      }
    },
    [fetchPendingMembers]
  );

  /**
   * Delete a pending member
   */
  const deletePendingMember = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error: deleteError } = await supabase
          .from("pending_members")
          .delete()
          .eq("id", id);

        if (deleteError) throw deleteError;

        await fetchPendingMembers();
        return true;
      } catch (err) {
        console.error("Error deleting pending member:", err);
        setError("Kon lid niet verwijderen");
        return false;
      }
    },
    [fetchPendingMembers]
  );

  /**
   * Manually link a user to a pending member
   */
  const linkUserToPendingMember = useCallback(
    async (userId: string, pendingMemberId: string): Promise<boolean> => {
      try {
        const { error: rpcError } = await supabase.rpc("link_user_to_pending_member", {
          p_user_id: userId,
          p_pending_member_id: pendingMemberId,
        });

        if (rpcError) throw rpcError;

        await fetchPendingMembers();
        return true;
      } catch (err: any) {
        console.error("Error linking user to pending member:", err);
        setError(err.message || "Kon gebruiker niet koppelen");
        return false;
      }
    },
    [fetchPendingMembers]
  );

  // Calculate stats
  const stats = {
    total: pendingMembers.length,
    claimed: pendingMembers.filter((m) => m.claimed_at !== null).length,
    pending: pendingMembers.filter((m) => m.claimed_at === null).length,
  };

  // Fetch on mount
  useEffect(() => {
    fetchPendingMembers();
  }, [fetchPendingMembers]);

  return {
    pendingMembers,
    isLoading,
    error,
    fetchPendingMembers,
    createPendingMember,
    updatePendingMember,
    deletePendingMember,
    linkUserToPendingMember,
    stats,
  };
};

